// Dynamic import of api is used inside fetchAnalyticsForPeriod for optimal code-splitting and standalone pure testability

export const formatDateRu = (dateStr) => {
  if (!dateStr) return ''
  const cleanStr = typeof dateStr === 'string' ? dateStr.split('T')[0] : ''
  const parts = cleanStr.split('-')
  if (parts.length === 3) {
    return `${parts[2]}.${parts[1]}.${parts[0]}`
  }
  return dateStr
}

export const formatMoney = (val) =>
  new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(val || 0)

export const formatMoneyExact = (val) =>
  new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val || 0)

/**
 * Pure calculation function that derives all Analytics metrics and tables.
 * Must produce 100% identical results to AnalyticsPage.jsx.
 */
export function computeAnalyticsMetrics({
  incomes = [],
  expenses = [],
  reserves = [],
  fixedPlans = [],
  payments = [],
  deductions = [],
  salaryList = [],
  baseSalaryAccrued = 0,
  issuedLogs = [],
  partners = [],
  partnerTurnovers = [],
  filterStartDate,
  filterEndDate,
  useEstimatedRevenue = false,
  forecastAutoCalc = false,
  forecastManualRate = 3.0,
  averageDailyPayrollPast30 = 0,
  averageDailyVarExpensesPast30 = 0,
  averageDailyPartnerCommissionPast30 = 0,
}) {
  const days = []
  const startParts = filterStartDate.split('-').map(Number)
  const endParts = filterEndDate.split('-').map(Number)
  let currentDay = new Date(startParts[0], startParts[1] - 1, startParts[2])
  const endDay = new Date(endParts[0], endParts[1] - 1, endParts[2])

  while (currentDay <= endDay) {
    const yyyy = currentDay.getFullYear()
    const mm = String(currentDay.getMonth() + 1).padStart(2, '0')
    const dd = String(currentDay.getDate()).padStart(2, '0')
    days.push(`${yyyy}-${mm}-${dd}`)
    currentDay.setDate(currentDay.getDate() + 1)
  }

  // 1. Ежедневная карта фактических доходов WB
  const dailyFactMap = {}
  const actualWbIncomes = incomes.filter(i => (i.source === 'Wildberries' || !i.source))

  actualWbIncomes.forEach(i => {
    const startStr = i.period_start || i.date
    const endStr = i.period_end || i.date
    if (!startStr || !endStr) return

    const sParts = startStr.split('-').map(Number)
    const eParts = endStr.split('-').map(Number)
    const sDate = new Date(sParts[0], sParts[1] - 1, sParts[2])
    const eDate = new Date(eParts[0], eParts[1] - 1, eParts[2])

    const daysInPeriod = []
    let cur = new Date(sDate)
    while (cur <= eDate) {
      const y = cur.getFullYear()
      const m = String(cur.getMonth() + 1).padStart(2, '0')
      const d = String(cur.getDate()).padStart(2, '0')
      daysInPeriod.push(`${y}-${m}-${d}`)
      cur.setDate(cur.getDate() + 1)
    }

    const N = daysInPeriod.length
    if (N === 0) return

    const amountTotal = Number(i.amount || 0) + Number(i.compensation || 0) + Number(i.bonus || 0) - Number(i.penalty || 0)
    const turnoverTotal = Number(i.turnover || 0) > 0 ? Number(i.turnover) : amountTotal / 0.03
    const saleByTariffTotal = Number(i.sale_by_tariff || 0) > 0 ? Number(i.sale_by_tariff) : turnoverTotal * 0.03

    const periodIssuedLogs = issuedLogs.filter(l => l.work_date >= startStr && l.work_date <= endStr)
    const totalIssuedCount = periodIssuedLogs.reduce((sum, l) => sum + (Number(l.count) || 0), 0)

    daysInPeriod.forEach(d => {
      let ratio = 1 / N
      if (totalIssuedCount > 0) {
        const dayLogs = periodIssuedLogs.filter(l => l.work_date === d)
        const dayCount = dayLogs.reduce((sum, l) => sum + (Number(l.count) || 0), 0)
        ratio = dayCount / totalIssuedCount
      }

      if (!dailyFactMap[d]) {
        dailyFactMap[d] = { income: 0, turnover: 0, sale_by_tariff: 0 }
      }
      dailyFactMap[d].income += amountTotal * ratio
      dailyFactMap[d].turnover += turnoverTotal * ratio
      dailyFactMap[d].sale_by_tariff += saleByTariffTotal * ratio
    })
  })

  let lastActualDate = null
  if (actualWbIncomes.length > 0) {
    lastActualDate = actualWbIncomes.reduce((max, i) => {
      const val = i.period_end || i.date
      return val > max ? val : max
    }, actualWbIncomes[0].period_end || actualWbIncomes[0].date)
  }

  let averageDailyTurnover = 0
  if (lastActualDate) {
    const lastDateObj = new Date(lastActualDate + 'T00:00:00')
    let count = 0
    let sum = 0
    for (let offset = 0; offset < 30; offset++) {
      const dObj = new Date(lastDateObj.getTime())
      dObj.setDate(dObj.getDate() - offset)
      const yyyy = dObj.getFullYear()
      const mm = String(dObj.getMonth() + 1).padStart(2, '0')
      const dd = String(dObj.getDate()).padStart(2, '0')
      const dateStr = `${yyyy}-${mm}-${dd}`

      if (dailyFactMap[dateStr]) {
        sum += dailyFactMap[dateStr].turnover
        count++
      }
    }
    if (count > 0) {
      averageDailyTurnover = sum / count
    }
  }

  if (averageDailyTurnover === 0) {
    const totalIssued = issuedLogs.reduce((acc, l) => acc + (l.count || 0), 0)
    const totalDays = issuedLogs.length || 1
    averageDailyTurnover = (totalIssued / totalDays) * 2500
  }
  if (averageDailyTurnover === 0) {
    averageDailyTurnover = 150000
  }

  const getTurnoverForDate = (dateStr) => {
    if (dailyFactMap[dateStr]) {
      return dailyFactMap[dateStr].turnover
    }
    if (lastActualDate && dateStr <= lastActualDate) {
      return 0
    }
    const logMatches = issuedLogs.filter(l => l.work_date === dateStr)
    const dayCount = logMatches.reduce((sum, l) => sum + (Number(l.count) || 0), 0)
    if (dayCount > 0) {
      return dayCount * 2500
    }
    return averageDailyTurnover
  }

  const dailyData = {}
  days.forEach(d => {
    let income = 0
    let turnover = 0
    let tariffPercent = 0
    let type = 'fact'

    if (lastActualDate && d <= lastActualDate) {
      type = 'fact'
      if (dailyFactMap[d]) {
        income = dailyFactMap[d].income
        turnover = dailyFactMap[d].turnover
        tariffPercent = turnover > 0 ? (dailyFactMap[d].sale_by_tariff / turnover) * 100 : 3.0
      } else {
        income = 0
        turnover = 0
        tariffPercent = 3.0
      }
    } else {
      type = 'forecast'
      if (forecastAutoCalc) {
        let rollingTurnover = 0
        const dObj = new Date(d + 'T00:00:00')
        for (let i = 1; i <= 30; i++) {
          const tObj = new Date(dObj.getTime())
          tObj.setDate(tObj.getDate() - i)
          const yyyy = tObj.getFullYear()
          const mm = String(tObj.getMonth() + 1).padStart(2, '0')
          const dd = String(tObj.getDate()).padStart(2, '0')
          const tStr = `${yyyy}-${mm}-${dd}`
          rollingTurnover += getTurnoverForDate(tStr)
        }
        tariffPercent = 3.885 - (rollingTurnover / 9000000)
        tariffPercent = Math.max(1.5, Math.min(4.5, tariffPercent))
      } else {
        tariffPercent = Number(forecastManualRate) || 3.0
      }

      const logMatches = issuedLogs.filter(l => l.work_date === d)
      const dayCount = logMatches.reduce((sum, l) => sum + (Number(l.count) || 0), 0)
      if (dayCount > 0) {
        turnover = dayCount * 2500
      } else {
        turnover = averageDailyTurnover
      }
      income = turnover * (tariffPercent / 100)
    }

    dailyData[d] = {
      date: d,
      income,
      turnover,
      tariffPercent,
      type
    }
  })

  const actualWbIncome = Object.values(dailyData)
    .filter(day => day.type === 'fact')
    .reduce((sum, day) => sum + day.income, 0)

  const estimatedWbIncome = Object.values(dailyData).reduce((sum, day) => sum + day.income, 0)
  const totalWbIncome = Math.round(useEstimatedRevenue ? estimatedWbIncome : actualWbIncome)

  const otherIncomeList = incomes.filter(
    i => i.date >= filterStartDate && i.date <= filterEndDate && i.source && i.source !== 'Wildberries'
  )
  const totalOtherIncome = Math.round(otherIncomeList.reduce((sum, i) => sum + Number(i.amount || 0), 0))
  const totalIncome = totalWbIncome + totalOtherIncome

  const displayedIncomes = incomes.filter(i => i.date >= filterStartDate && i.date <= filterEndDate)

  let maxIncomeDateStr = null
  if (displayedIncomes && displayedIncomes.length > 0) {
    let maxTime = -Infinity
    displayedIncomes.forEach(i => {
      const dateStr = i.period_end || i.date
      if (dateStr) {
        const time = new Date(dateStr + 'T23:59:59').getTime()
        if (time > maxTime) {
          maxTime = time
          maxIncomeDateStr = dateStr
        }
      }
    })
  }

  const selectedPeriodExpenses = expenses.filter(e => e.date >= filterStartDate && e.date <= filterEndDate)
  const selectedPeriodPartnerTurnovers = partnerTurnovers.filter(t => {
    const d = t.entry_date?.split('T')[0]
    return d >= filterStartDate && d <= filterEndDate
  })

  const forecastDaysCount = (useEstimatedRevenue && lastActualDate)
    ? days.filter(d => d > lastActualDate).length
    : 0

  const payrollTotal = Math.round((baseSalaryAccrued || 0) + (useEstimatedRevenue ? forecastDaysCount * averageDailyPayrollPast30 : 0))

  const varExpensesList = selectedPeriodExpenses.filter(e => e.type !== 'fixed_payment' && e.is_paid !== false)
  const actualVariableExpenses = varExpensesList.reduce((sum, e) => sum + Math.round(Number(e.amount) || 0), 0)
  const variableExpenses = actualVariableExpenses + (useEstimatedRevenue ? Math.round(forecastDaysCount * averageDailyVarExpensesPast30) : 0)

  const activePlans = fixedPlans.filter(p => p.is_active !== false)
  const fixedPlanTotal = Math.round(activePlans.reduce((sum, p) => sum + Number(p.amount || p.monthly_amount || 0), 0))

  const fixedPaidExpenses = Math.round(activePlans.reduce((sum, p) => {
    const amount = Number(p.amount || p.monthly_amount || 0)
    if (amount <= 0 || isNaN(amount)) return sum
    if (!maxIncomeDateStr) return sum

    const rStart = new Date(filterStartDate + 'T00:00:00')
    const rEnd = new Date(filterEndDate + 'T23:59:59')
    const cutoffDateStr = useEstimatedRevenue ? filterEndDate : (maxIncomeDateStr || filterStartDate)
    const cutoffEnd = new Date(Math.min(rEnd.getTime(), new Date(cutoffDateStr + 'T23:59:59').getTime()))

    if (rStart > cutoffEnd) return sum

    let proratedTotal = 0
    let cur = new Date(rStart.getFullYear(), rStart.getMonth(), 1)
    while (cur <= cutoffEnd) {
      const mYear = cur.getFullYear()
      const mMonth = cur.getMonth()
      const daysInMonth = new Date(mYear, mMonth + 1, 0).getDate()

      const mStart = new Date(mYear, mMonth, 1)
      const mEnd = new Date(mYear, mMonth + 1, 0, 23, 59, 59)

      const overlapStart = Math.max(mStart.getTime(), rStart.getTime())
      const overlapEnd = Math.min(mEnd.getTime(), cutoffEnd.getTime())

      if (overlapStart <= overlapEnd) {
        const overlapDays = Math.ceil((overlapEnd - overlapStart) / (1000 * 60 * 60 * 24))
        proratedTotal += (amount / daysInMonth) * overlapDays
      }
      cur.setMonth(cur.getMonth() + 1)
    }
    return sum + proratedTotal
  }, 0))

  const actualReserves = reserves.reduce((sum, r) => sum + Math.round(Number(r.amount) || 0), 0)
  const taxAmount = Math.round(totalIncome * 0.06)

  const actualPartnerCommission = selectedPeriodPartnerTurnovers.reduce(
    (sum, t) => sum + Math.round(Number(t.commission_amount || 0)),
    0
  )
  const totalPartnerCommission =
    actualPartnerCommission + (useEstimatedRevenue ? Math.round(forecastDaysCount * averageDailyPartnerCommissionPast30) : 0)

  const totalExpensesAll = payrollTotal + variableExpenses + fixedPaidExpenses + taxAmount + totalPartnerCommission
  const actualFreeCashFlow = totalIncome - totalExpensesAll
  const profitMarginPct = totalIncome > 0 ? (actualFreeCashFlow / totalIncome) * 100 : 0

  const pctPayroll = totalExpensesAll > 0 ? (payrollTotal / totalExpensesAll) * 100 : 0
  const pctFixed = totalExpensesAll > 0 ? (fixedPaidExpenses / totalExpensesAll) * 100 : 0
  const pctVar = totalExpensesAll > 0 ? (variableExpenses / totalExpensesAll) * 100 : 0
  const pctTax = totalExpensesAll > 0 ? (taxAmount / totalExpensesAll) * 100 : 0
  const pctPartner = totalExpensesAll > 0 ? (totalPartnerCommission / totalExpensesAll) * 100 : 0

  // 2. Посуточная динамика (Flow Chart) — Строгая математическая сходимость со сводкой (SSOT)
  const dailyMap = {}
  days.forEach(d => {
    dailyMap[d] = {
      date: d,
      income: 0,
      expense: 0,
      fixedExpense: 0,
      varExpense: 0,
      tax: 0,
      partnerCommission: 0,
      payroll: 0,
      reserve: 0
    }
  })

  // 1) Доходы
  if (useEstimatedRevenue) {
    days.forEach(d => {
      const dayFin = dailyData[d]
      if (dayFin && dailyMap[d]) {
        dailyMap[d].income += Math.round(Number(dayFin.income) || 0)
      }
    })
    otherIncomeList.forEach(i => {
      if (dailyMap[i.date]) {
        dailyMap[i.date].income += Math.round(Number(i.amount || 0))
      }
    })
  } else {
    days.forEach(d => {
      const dayFin = dailyData[d]
      if (dayFin && dayFin.type === 'fact' && dailyMap[d]) {
        const inc = Math.round(Number(dayFin.income) || 0)
        if (inc > 0) {
          dailyMap[d].income += inc
        }
      }
    })
    otherIncomeList.forEach(i => {
      if (dailyMap[i.date]) {
        dailyMap[i.date].income += Math.round(Number(i.amount || 0))
      }
    })
  }
  // Устранение копеечного расхождения между суммой дней и totalIncome
  const totalDailyIncome = days.reduce((s, d) => s + dailyMap[d].income, 0)
  const incomeDiff = totalIncome - totalDailyIncome
  if (incomeDiff !== 0) {
    const daysWithIncome = days.filter(d => dailyMap[d].income > 0)
    if (daysWithIncome.length > 0) {
      dailyMap[daysWithIncome[daysWithIncome.length - 1]].income += incomeDiff
    } else if (days.length > 0) {
      dailyMap[days[days.length - 1]].income += incomeDiff
    }
  }

  // Налог УСН 6% строго распределяется по дням с доходом в целых рублях
  const totalTaxInt = Math.round(taxAmount)
  const daysWithIncome = days.filter(d => dailyMap[d].income > 0)
  if (daysWithIncome.length > 0 && totalTaxInt > 0) {
    let allocatedTax = 0
    daysWithIncome.forEach((d, idx) => {
      if (idx === daysWithIncome.length - 1) {
        dailyMap[d].tax = Math.max(0, totalTaxInt - allocatedTax)
      } else {
        const val = Math.round(dailyMap[d].income * 0.06)
        dailyMap[d].tax = val
        allocatedTax += val
      }
    })
  }

  // 2) Постоянные расходы: распределяются по активным дням до cutoffDate
  const cutoffDateStr = useEstimatedRevenue ? filterEndDate : (maxIncomeDateStr || filterStartDate)
  const eligibleDaysForFixed = days.filter(d => !maxIncomeDateStr || d <= cutoffDateStr)
  if (eligibleDaysForFixed.length > 0 && fixedPaidExpenses > 0) {
    const totalFixedInt = Math.round(fixedPaidExpenses)
    const baseDaily = Math.floor(totalFixedInt / eligibleDaysForFixed.length)
    const remainder = totalFixedInt % eligibleDaysForFixed.length
    eligibleDaysForFixed.forEach((d, idx) => {
      dailyMap[d].fixedExpense = baseDaily + (idx < remainder ? 1 : 0)
    })
  }

  // 3) Переменные расходы (по датам чеков)
  selectedPeriodExpenses.filter(e => !e.fixed_plan_id && e.is_paid !== false).forEach(e => {
    if (dailyMap[e.date]) {
      dailyMap[e.date].varExpense += Math.round(Number(e.amount) || 0)
    }
  })

  // 4) Партнёрские комиссии
  selectedPeriodPartnerTurnovers.forEach(t => {
    const d = t.entry_date?.split('T')[0]
    if (d && dailyMap[d]) {
      dailyMap[d].partnerCommission += Math.round(Number(t.commission_amount) || 0)
    }
  })

  // 5) ФОТ: собираем смены и нормализуем под итоговый payrollTotal (с премиями/штрафами)
  const rawDailyPayroll = {}
  days.forEach(d => { rawDailyPayroll[d] = 0 })
  let rawDailyPayrollSum = 0

  salaryList.forEach(sal => {
    if (sal.dailyDetails) {
      Object.entries(sal.dailyDetails).forEach(([date, dayData]) => {
        if (rawDailyPayroll[date] !== undefined) {
          const dayTotal = Number(dayData?.total) || 0
          if (dayTotal > 0) {
            rawDailyPayroll[date] += dayTotal
            rawDailyPayrollSum += dayTotal
          }
        }
      })
    }
  })

  const totalPayrollInt = Math.round(payrollTotal)
  if (totalPayrollInt > 0) {
    if (rawDailyPayrollSum > 0) {
      const ratio = totalPayrollInt / rawDailyPayrollSum
      let allocatedPayroll = 0
      const daysWithShifts = days.filter(d => rawDailyPayroll[d] > 0)
      days.forEach(d => {
        if (rawDailyPayroll[d] > 0) {
          const isLast = (d === daysWithShifts[daysWithShifts.length - 1])
          if (isLast) {
            dailyMap[d].payroll = Math.max(0, totalPayrollInt - allocatedPayroll)
          } else {
            const val = Math.round(rawDailyPayroll[d] * ratio)
            dailyMap[d].payroll = val
            allocatedPayroll += val
          }
        }
      })
    } else {
      // Если смен в табеле нет, но начислен фиксированный ФОТ
      const baseDaily = Math.floor(totalPayrollInt / days.length)
      const remainder = totalPayrollInt % days.length
      days.forEach((d, idx) => {
        dailyMap[d].payroll = baseDaily + (idx < remainder ? 1 : 0)
      })
    }
  }

  // 6) Резервы: отслеживаются отдельно как аллокация прибыли, НЕ включаются в операционные расходы!
  reserves.forEach(r => {
    const d = r.date || r.created_at?.split('T')[0]
    if (d && dailyMap[d]) {
      dailyMap[d].reserve += Math.round(Number(r.amount) || 0)
    }
  })

  // Итоговый расход дня = ФОТ + Постоянные + Переменные + Налог + Партнёры
  days.forEach(d => {
    dailyMap[d].expense =
      dailyMap[d].payroll +
      dailyMap[d].varExpense +
      dailyMap[d].fixedExpense +
      dailyMap[d].tax +
      dailyMap[d].partnerCommission
  })

  const flowChart = days.map(d => {
    const inc = Number(dailyMap[d].income) || 0
    const exp = Number(dailyMap[d].expense) || 0
    const actualProfit = inc - exp
    const dateParts = d.split('-').map(Number)
    const dateObj = new Date(dateParts[0], dateParts[1] - 1, dateParts[2])
    const daysOfWeek = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб']
    const dayName = daysOfWeek[dateObj.getDay()]

    return {
      date: d,
      income: inc,
      expense: exp,
      profit: Math.max(0, actualProfit),
      realProfit: actualProfit,
      reserve: dailyMap[d].reserve,
      displayDate: `${d.split('-').slice(1).reverse().join('.')} ${dayName}`
    }
  })

  // 3. Структура затрат
  const expenseStruct = []
  if (payrollTotal > 0) expenseStruct.push({ name: 'ФОТ (Зарплаты)', value: payrollTotal, color: '#F59E0B', pct: pctPayroll })
  if (variableExpenses > 0) expenseStruct.push({ name: 'Переменные расходы', value: variableExpenses, color: '#EF4444', pct: pctVar })
  if (fixedPaidExpenses > 0) expenseStruct.push({ name: 'Постоянные расходы', value: fixedPaidExpenses, color: '#10B981', pct: pctFixed })
  if (taxAmount > 0) expenseStruct.push({ name: 'Налог УСН 6%', value: taxAmount, color: '#3B82F6', pct: pctTax })
  if (totalPartnerCommission > 0) expenseStruct.push({ name: 'Партнёры', value: totalPartnerCommission, color: '#8B5CF6', pct: pctPartner })

  const hasAnyData =
    incomes.length > 0 ||
    expenses.length > 0 ||
    fixedPlans.length > 0 ||
    reserves.length > 0 ||
    partnerTurnovers.length > 0 ||
    salaryList.length > 0

  return {
    totalIncome,
    totalWbIncome,
    totalOtherIncome,
    totalExpensesAll,
    payrollTotal,
    variableExpenses,
    fixedPaidExpenses,
    fixedPlanTotal,
    taxAmount,
    totalPartnerCommission,
    actualFreeCashFlow,
    netProfit: actualFreeCashFlow,
    ebitda: actualFreeCashFlow + taxAmount,
    reservesTotal: actualReserves,
    netCashFlowAfterReserves: actualFreeCashFlow - actualReserves,
    profitMarginPct,
    pctPayroll,
    pctFixed,
    pctVar,
    pctTax,
    pctPartner,
    days,
    dailyData,
    flowChart,
    expenseStruct,
    hasAnyData,
    displayedIncomes,
    selectedPeriodExpenses,
    selectedPeriodPartnerTurnovers,
    salaryList,
    activePlans,
    lastActualDate
  }
}

/**
 * Loads analytics data for an arbitrary period using the exact same endpoints as AnalyticsPage.
 */
export async function fetchAnalyticsForPeriod({
  activePvz,
  filterStartDate,
  filterEndDate,
  useEstimatedRevenue = false,
  forecastAutoCalc = false,
  forecastManualRate = 3.0
}) {
  const pvzId = activePvz?.id
  if (!pvzId) throw new Error('Не выбран активный ПВЗ')

  const api = await import('../../../lib/api')

  const startParts = filterStartDate.split('-').map(Number)
  const startDateObj = new Date(startParts[0], startParts[1] - 1, startParts[2])
  startDateObj.setDate(startDateObj.getDate() - 30)
  const pStartMinus30 = `${startDateObj.getFullYear()}-${String(startDateObj.getMonth() + 1).padStart(2, '0')}-${String(startDateObj.getDate()).padStart(2, '0')}`

  const [inc, exp, pay, ded, emps, rsv, plans, sets, issued, salCfg] = await Promise.all([
    api.getWbIncomes(pvzId, pStartMinus30, filterEndDate),
    api.getExpenses(pvzId, pStartMinus30, filterEndDate),
    api.getPayments({ periodStart: filterStartDate, periodEnd: filterEndDate, pvzId }),
    api.getDeductions(filterStartDate, filterEndDate, pvzId),
    api.getEmployeesList(pvzId),
    api.getReserves(pvzId, filterStartDate, filterEndDate),
    api.getFixedExpenseTemplates(pvzId),
    api.getAnalyticsSettings(pvzId),
    api.getWorkLogs('issued', { startDate: pStartMinus30, endDate: filterEndDate, pvzId }),
    api.getSalaryConfig()
  ])

  const [rawSalaries, rawPastSalaries] = await Promise.all([
    api.calculateTeamSalaries(pvzId, filterStartDate, filterEndDate, (emps || []).filter(e => !e.is_legacy), [], { mode: 'analytics' }),
    api.calculateTeamSalaries(pvzId, pStartMinus30, filterStartDate, (emps || []).filter(e => !e.is_legacy), [], { mode: 'analytics' })
  ])

  const salaries = (rawSalaries || []).map(sal => ({
    ...sal,
    shiftsCount: sal.daysWorked
  }))

  const calcPayroll = salaries.reduce((sum, s) => sum + Math.max(s.total || 0, 0), 0)

  const pastPayrollTotal = (rawPastSalaries || []).reduce((sum, s) => sum + Math.max(s.total || 0, 0), 0)
  const avgDailyPayroll = pastPayrollTotal / 30

  const pastExpenses = (exp || []).filter(
    e => e.date >= pStartMinus30 && e.date < filterStartDate && e.type !== 'fixed_payment' && e.is_paid !== false
  )
  const pastVarExpensesTotal = pastExpenses.reduce((sum, e) => sum + Number(e.amount), 0)
  const avgDailyVarExpenses = pastVarExpensesTotal / 30

  let pts = []
  let ptTurnovers = []
  let avgDailyPartnerCommission = 0

  try {
    const [partnersRes, partnerTurnoversRes] = await Promise.all([
      api.getPartners(pvzId),
      api.getPartnerTurnovers(pvzId, pStartMinus30, filterEndDate)
    ])
    pts = partnersRes || []
    ptTurnovers = partnerTurnoversRes || []

    const pastPartnerTurnovers = ptTurnovers.filter(t => {
      const d = t.entry_date?.split('T')[0]
      return d >= pStartMinus30 && d < filterStartDate
    })
    const pastPartnerTotal = pastPartnerTurnovers.reduce((sum, t) => sum + Number(t.commission_amount || 0), 0)
    avgDailyPartnerCommission = pastPartnerTotal / 30
  } catch (e) {
    console.warn('[AnalyticsExport] Partners load error:', e)
  }

  const isAuto = salCfg?.wbRevenueAutoCalc !== undefined ? salCfg.wbRevenueAutoCalc : forecastAutoCalc
  const manualRate = salCfg?.wbRevenuePerIssued !== undefined ? salCfg.wbRevenuePerIssued : forecastManualRate

  const metrics = computeAnalyticsMetrics({
    incomes: inc || [],
    expenses: exp || [],
    reserves: rsv || [],
    fixedPlans: plans || [],
    payments: pay || [],
    deductions: ded || [],
    salaryList: salaries,
    baseSalaryAccrued: calcPayroll,
    issuedLogs: issued || [],
    partners: pts,
    partnerTurnovers: ptTurnovers,
    filterStartDate,
    filterEndDate,
    useEstimatedRevenue,
    forecastAutoCalc: isAuto,
    forecastManualRate: manualRate,
    averageDailyPayrollPast30: avgDailyPayroll,
    averageDailyVarExpensesPast30: avgDailyVarExpenses,
    averageDailyPartnerCommissionPast30: avgDailyPartnerCommission,
  })

  return {
    metrics,
    rawData: {
      incomes: inc || [],
      expenses: exp || [],
      reserves: rsv || [],
      fixedPlans: plans || [],
      payments: pay || [],
      deductions: ded || [],
      salaryList: salaries,
      issuedLogs: issued || [],
      partners: pts,
      partnerTurnovers: ptTurnovers
    }
  }
}
